import { queryOptions, useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  ListMediaQuery,
  MediaAdminDetail,
  MediaAdminItem,
  MediaItem,
  Paginated,
} from '@news-cms/shared';
import { ApiError, api } from './api';
import { GifNotSupportedError, convertImageToWebp } from './image-convert';
import { toast } from '@/components/ui/toaster';

const apiBaseUrl = import.meta.env.VITE_API_URL ?? 'http://localhost:8787';

/** Build absolute URL from a media item's relative `url` path. */
export function mediaAbsoluteUrl(path: string): string {
  if (/^https?:\/\//i.test(path)) return path;
  return `${apiBaseUrl}${path}`;
}

/**
 * Match an `<img src="...api/public/media/...">` attribute in HTML.
 * Captures three groups for surgical replace: (prefix up to src=", URL, suffix).
 */
const MEDIA_IMG_RE =
  /(<img\b[^>]*?\bsrc\s*=\s*["'])([^"']*\/api\/public\/media\/[^"']+)(["'][^>]*>)/gi;

/**
 * Convert `<img src="/api/public/media/...">` → absolute URL using the
 * configured `apiBaseUrl`. Leaves already-absolute URLs untouched.
 *
 * Why this exists: feed-import + backfill write RELATIVE paths into
 * body_html (so the saved post is portable across environments). The
 * admin SPA runs on a different origin than the worker, so relative
 * paths there resolve to the Pages host instead of the API host. This
 * transform makes the editor render correctly without changing what's
 * stored in the DB.
 */
export function absolutizeMediaUrls(html: string): string {
  return html.replace(MEDIA_IMG_RE, (_full, pre: string, src: string, post: string) => {
    if (/^https?:\/\//i.test(src)) return `${pre}${src}${post}`;
    return `${pre}${apiBaseUrl}${src}${post}`;
  });
}

/**
 * Inverse of `absolutizeMediaUrls` — strip ANY origin from
 * `<img src="https://.../api/public/media/...">` so the DB stays
 * portable. Applied to editor output before save.
 *
 * Permissive on hostname: doesn't care which host the absolute URL
 * uses (dev/staging/prod all differ); the marker is the path segment
 * `/api/public/media/`. Non-matching `<img>` tags (e.g. external
 * hot-linked images that weren't yet mirrored) are left alone.
 */
export function relativizeMediaUrls(html: string): string {
  return html.replace(MEDIA_IMG_RE, (_full, pre: string, src: string, post: string) => {
    const idx = src.indexOf('/api/public/media/');
    if (idx < 0) return `${pre}${src}${post}`;
    return `${pre}${src.slice(idx)}${post}`;
  });
}

export interface ImageDimensions {
  width: number;
  height: number;
}

/**
 * Read image dimensions client-side using a temporary `<img>` element.
 * Returns null if the browser can't decode (e.g., for non-image files).
 */
export function readImageDimensions(file: File): Promise<ImageDimensions | null> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const dims: ImageDimensions = {
        width: img.naturalWidth,
        height: img.naturalHeight,
      };
      URL.revokeObjectURL(url);
      resolve(dims);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(null);
    };
    img.src = url;
  });
}

export interface UploadMediaInput {
  file: File;
  alt?: string;
  width?: number;
  height?: number;
}

/**
 * Direct upload helper — same path as useUploadMedia's mutationFn,
 * but callable from non-hook contexts (e.g. TipTap paste handlers,
 * drag-drop drop handlers).
 */
export async function uploadMediaApi(input: UploadMediaInput): Promise<MediaItem> {
  // Normalize client-side (cap to 1200px + re-encode WebP q0.82) so the API
  // only ever stores reasonably-sized WebP — including already-WebP exports
  // saved at near-lossless quality. GIF throws; the mutation-level handler
  // toasts a friendly message.
  let file = input.file;
  try {
    file = await convertImageToWebp(input.file);
  } catch (err) {
    if (err instanceof GifNotSupportedError) throw err;
    // Other convert errors (canvas decode failure on weird file) →
    // fall back to original; backend will accept or reject by MIME.
    console.warn('image convert to webp failed, uploading original', err);
  }

  // Dimensions must match the STORED bytes — conversion may have downscaled, so
  // read them from the final file rather than trusting caller-passed dims (which
  // were measured on the original). Fall back to the passed values if the read
  // fails (e.g. non-image file the backend will reject anyway).
  const dims = await readImageDimensions(file);
  const width = dims?.width ?? input.width;
  const height = dims?.height ?? input.height;

  const fd = new FormData();
  fd.append('file', file, file.name);
  if (input.alt) fd.append('alt', input.alt);
  if (width != null) fd.append('width', String(width));
  if (height != null) fd.append('height', String(height));

  const res = await fetch(`${apiBaseUrl}/api/admin/media/upload`, {
    method: 'POST',
    credentials: 'include',
    body: fd,
  });

  const json = (await res.json().catch(() => null)) as
    | { data: { media: MediaItem } }
    | { error: { code: string; message: string } }
    | null;

  if (!res.ok || !json || 'error' in json) {
    const code = json && 'error' in json ? json.error.code : 'NETWORK_ERROR';
    const message = json && 'error' in json ? json.error.message : `Upload failed (${res.status})`;
    throw new ApiError(code, message, res.status);
  }

  return json.data.media;
}

function qs(filter: Partial<ListMediaQuery>): string {
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(filter)) {
    if (v === undefined || v === null || v === '') continue;
    params.set(k, String(v));
  }
  return params.toString();
}

/** Server returns a Paginated payload + an extra `totalBytes` field —
 *  the sum of `size` across the same filter the list uses. Drives the
 *  storage-usage line on the media page header. */
export interface MediaListResponse extends Paginated<MediaAdminItem> {
  totalBytes: number;
}

export const mediaListQueryOptions = (filter: Partial<ListMediaQuery>) =>
  queryOptions({
    queryKey: ['media', 'list', filter] as const,
    queryFn: () => api<MediaListResponse>(`/api/admin/media?${qs(filter)}`),
  });

export const mediaDetailQueryOptions = (id: string) =>
  queryOptions({
    queryKey: ['media', 'detail', id] as const,
    queryFn: () => api<{ media: MediaAdminDetail }>(`/api/admin/media/${id}`).then((d) => d.media),
  });

/** Patch descriptive metadata (altText, caption) on an existing media
 *  row. File content is immutable — to change pixels you upload a new
 *  media item. Use `null` to clear a field. */
export function useUpdateMedia() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      ...input
    }: {
      id: string;
      altText?: string | null;
      caption?: string | null;
    }) => {
      await api<{ ok: true }>(`/api/admin/media/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(input),
      });
      return id;
    },
    onSuccess: (id) => {
      void qc.invalidateQueries({ queryKey: ['media', 'detail', id] });
      void qc.invalidateQueries({ queryKey: ['media', 'list'] });
      toast.success('อัปเดต metadata แล้ว');
    },
    onError: (e) => {
      if (e instanceof ApiError && e.code === 'FORBIDDEN') {
        toast.error('คุณไม่มีสิทธิ์แก้ไข metadata');
        return;
      }
      toast.error(e instanceof Error ? e.message : 'อัปเดตไม่สำเร็จ');
    },
  });
}

export function useDeleteMedia() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api<{ ok: true }>(`/api/admin/media/${id}`, { method: 'DELETE' });
      return id;
    },
    onSuccess: (id) => {
      qc.removeQueries({ queryKey: ['media', 'detail', id] });
      void qc.invalidateQueries({ queryKey: ['media', 'list'] });
      toast.success('ลบสื่อเรียบร้อย');
    },
    onError: (e) => {
      if (e instanceof ApiError && e.code === 'MEDIA_IN_USE') {
        toast.error('สื่อนี้ยังถูกใช้ในบทความ — ลบออกจากบทความก่อน');
        return;
      }
      if (e instanceof ApiError && e.code === 'FORBIDDEN') {
        toast.error('คุณไม่มีสิทธิ์ลบสื่อ');
        return;
      }
      toast.error(e instanceof Error ? e.message : 'ลบไม่สำเร็จ');
    },
  });
}

interface BulkDeleteResult {
  deletedCount: number;
  rejectedCount: number;
  rejectedIds: string[];
}

/**
 * Bulk-delete by id. Single round-trip to /_bulk-delete. Always
 * invalidates the list cache + the detail cache for every input id
 * (some may have succeeded, some not — easier to flush all than
 * track per-id). Aggregated toast surfaces the partial-success
 * shape so the caller doesn't need extra UI for it.
 */
export function useBulkDeleteMedia() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (ids: string[]) => {
      const data = await api<BulkDeleteResult>(`/api/admin/media/_bulk-delete`, {
        method: 'POST',
        body: JSON.stringify({ ids }),
      });
      return { ...data, requestedIds: ids };
    },
    onSuccess: ({ deletedCount, rejectedCount, requestedIds }) => {
      for (const id of requestedIds) {
        qc.removeQueries({ queryKey: ['media', 'detail', id] });
      }
      void qc.invalidateQueries({ queryKey: ['media', 'list'] });
      if (rejectedCount === 0) {
        toast.success(`ลบเรียบร้อย ${deletedCount} ไฟล์`);
      } else {
        toast.warning(`ลบ ${deletedCount} ไฟล์ · ข้าม ${rejectedCount} (ยังถูกใช้ในบทความ)`);
      }
    },
    onError: (e) => {
      toast.error(e instanceof Error ? e.message : 'ลบไม่สำเร็จ');
    },
  });
}

export function useUploadMedia() {
  return useMutation({
    mutationFn: uploadMediaApi,
    onError: (e) => {
      if (e instanceof GifNotSupportedError) {
        toast.error('GIF ไม่รองรับ — แปลงเป็น MP4 แทน');
        return;
      }
      if (e instanceof ApiError) {
        switch (e.code) {
          case 'INVALID_MIME':
            toast.error('ประเภทไฟล์ไม่รองรับ (JPEG / PNG / WebP / AVIF)');
            return;
          case 'FILE_TOO_LARGE':
            toast.error('ไฟล์ใหญ่เกินไป (สูงสุด 25 MB)');
            return;
          case 'EMPTY_FILE':
            toast.error('ไฟล์ว่าง');
            return;
          case 'FORBIDDEN':
            toast.error('คุณไม่มีสิทธิ์อัปโหลดสื่อ');
            return;
          default:
            toast.error(e.message || 'อัปโหลดไม่สำเร็จ');
            return;
        }
      }
      toast.error('อัปโหลดไม่สำเร็จ');
    },
  });
}
